import { useState, useEffect } from "react";
import { useCoEConfig } from "@/hooks/useCoEConfig";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Eye,
  Target,
  ShieldCheck,
  FileText,
  Workflow,
  Save,
  Plus,
  X,
  Zap,
  RefreshCw,
  Package,
  Compass,
  ChevronRight,
  HelpCircle,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// === Value Vision Archetypes ===
const VALUE_VISIONS = [
  {
    id: "operative",
    name: "IA Operativa",
    subtitle: "La Visión de Eficiencia",
    icon: Zap,
    color: "bg-blue-500",
    colorLight: "bg-blue-500/10 text-blue-700 border-blue-200",
    focus: "Automatización de tareas específicas y repetitivas.",
    description:
      'Se centra en "hacer lo mismo, pero más rápido y barato". Utiliza la IA para eliminar la carga administrativa y los errores manuales en tareas aisladas.',
    value: "Reducción de costos operativos (OPEX) y liberación de tiempo del personal.",
    kpi: "Horas hombre ahorradas / Reducción de costo por transacción.",
    risk: "Bajo",
    horizon: "Corto plazo (Semanas)",
    objective: "Ahorro",
  },
  {
    id: "processes",
    name: "IA de Procesos",
    subtitle: "La Visión de Agilidad",
    icon: RefreshCw,
    color: "bg-green-500",
    colorLight: "bg-green-500/10 text-green-700 border-green-200",
    focus: "Rediseño y optimización de flujos de trabajo completos.",
    description:
      'Cambia "cómo se trabaja". La IA actúa como un tejido conector que coordina diferentes áreas, permitiendo que la empresa reaccione en tiempo real.',
    value: "Incremento drástico en la velocidad de respuesta y la capacidad instalada sin aumentar el personal.",
    kpi: "Reducción del Time-to-Market / Aumento del rendimiento del proceso (throughput).",
    risk: "Medio",
    horizon: "Medio plazo (Meses)",
    objective: "Agilidad",
  },
  {
    id: "product",
    name: "IA de Producto y Modelo",
    subtitle: "La Visión de Reinvención",
    icon: Package,
    color: "bg-purple-500",
    colorLight: "bg-purple-500/10 text-purple-700 border-purple-200",
    focus: "Transformación de la oferta comercial y el núcleo del producto.",
    description:
      'Cambia "qué vendemos". La IA se integra en el producto para hacerlo inteligente o permite nuevos modelos de negocio.',
    value: "Creación de nuevas fuentes de ingresos, hiper-personalización y diferenciación competitiva.",
    kpi: "Ingresos por nuevos productos de IA / Incremento del Customer Lifetime Value (LTV).",
    risk: "Alto",
    horizon: "Largo plazo (Años)",
    objective: "Ingresos",
  },
  {
    id: "strategic",
    name: "IA Estratégica",
    subtitle: "La Visión de Decisión",
    icon: Compass,
    color: "bg-orange-500",
    colorLight: "bg-orange-500/10 text-orange-700 border-orange-200",
    focus: "Inteligencia predictiva para la alta dirección.",
    description:
      'Actúa como el "Oráculo" de la empresa. Utiliza modelos avanzados para predecir tendencias de mercado, riesgos financieros y oportunidades de inversión.',
    value: "Supervivencia a largo plazo mediante la toma de decisiones informadas y la mitigación proactiva de riesgos.",
    kpi: "Precisión de los pronósticos estratégicos / Reducción de pérdidas por riesgos no detectados.",
    risk: "Medio",
    horizon: "Continuo",
    objective: "Dirección",
  },
];

// === Predefined Principles ===
const DEFAULT_PRINCIPLES = [
  { id: "etica", label: "Ética", description: "Asegurar que la IA opera bajo estándares morales y de integridad" },
  { id: "transparencia", label: "Transparencia", description: "Garantizar visibilidad en cómo la IA toma decisiones" },
  { id: "explicabilidad", label: "Explicabilidad", description: "Los modelos deben poder explicar sus resultados" },
  { id: "seguridad", label: "Seguridad", description: "Proteger sistemas de IA contra amenazas y vulnerabilidades" },
  { id: "privacidad", label: "Privacidad", description: "Respetar y proteger los datos personales de usuarios" },
  { id: "supervision-humana", label: "Supervisión Humana", description: "Mantener control humano sobre decisiones críticas" },
  { id: "equidad", label: "Equidad", description: "Evitar sesgos y discriminación en los resultados de la IA" },
  { id: "utilidad-sobre-novedad", label: "Utilidad sobre Novedad", description: "Priorizar soluciones que generen valor real sobre tendencias" },
  { id: "transformacion", label: "Transformación", description: "Impulsar cambio organizacional significativo con IA" },
  { id: "sostenibilidad", label: "Sostenibilidad", description: "Considerar el impacto ambiental y social de la IA" },
  { id: "inclusion", label: "Inclusión", description: "Diseñar soluciones de IA accesibles para todos" },
  { id: "responsabilidad", label: "Responsabilidad (Accountability)", description: "Definir claramente quién responde por cada sistema de IA" },
];

interface CoEStrategicConfigProps {
  organizationId: string;
}

export function CoEStrategicConfig({ organizationId }: CoEStrategicConfigProps) {
  const { config, loading, saveConfig, isSaving } = useCoEConfig(organizationId);

  // Value vision
  const [selectedVision, setSelectedVision] = useState<string>("operative");
  const [visionStatement, setVisionStatement] = useState("");

  // Objective
  const [objective, setObjective] = useState("");

  // Principles
  const [selectedPrinciples, setSelectedPrinciples] = useState<string[]>([
    "etica",
    "transparencia",
    "explicabilidad",
    "seguridad",
    "privacidad",
    "supervision-humana",
    "equidad",
  ]);
  const [customPrinciples, setCustomPrinciples] = useState<{ id: string; label: string; description: string }[]>([]);
  const [newPrincipleOpen, setNewPrincipleOpen] = useState(false);
  const [newPrincipleName, setNewPrincipleName] = useState("");
  const [newPrincipleDesc, setNewPrincipleDesc] = useState("");

  // Strategic plan
  const [strategicPlan, setStrategicPlan] = useState("");

  // Objectives
  const [objectives, setObjectives] = useState<string[]>([]);
  const [newObjective, setNewObjective] = useState("");

  // Prioritized processes
  const [prioritizedProcesses, setPrioritizedProcesses] = useState<string[]>([]);
  const [newProcess, setNewProcess] = useState("");

  // Prioritized challenges (where AI is expected to provide a solution)
  const [prioritizedChallenges, setPrioritizedChallenges] = useState<string[]>([]);
  const [newChallenge, setNewChallenge] = useState("");

  // Initialize from DB
  useEffect(() => {
    if (config) {
      setSelectedVision(config.value_vision_archetype || "operative");
      setVisionStatement(config.vision || "");
      setObjective(config.objective || "");
      setSelectedPrinciples(config.selected_principles || [
        "etica",
        "transparencia",
        "explicabilidad",
        "seguridad",
        "privacidad",
        "supervision-humana",
        "equidad",
      ]);
      setCustomPrinciples(config.custom_principles || []);
      setStrategicPlan(config.strategic_plan || "");
      setPrioritizedProcesses(config.prioritized_processes || []);
      setPrioritizedChallenges(
        Array.isArray(config.prioritized_challenges)
          ? (config.prioritized_challenges as Array<{ title: string }>).map((c) => c.title).filter(Boolean)
          : []
      );
      setObjectives(config.objectives || []);
    }
  }, [config]);

  const togglePrinciple = (id: string) => {
    setSelectedPrinciples((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const addCustomPrinciple = () => {
    if (!newPrincipleName.trim()) return;
    const id = `custom-${Date.now()}`;
    setCustomPrinciples((prev) => [...prev, { id, label: newPrincipleName.trim(), description: newPrincipleDesc.trim() }]);
    setSelectedPrinciples((prev) => [...prev, id]);
    setNewPrincipleName("");
    setNewPrincipleDesc("");
    setNewPrincipleOpen(false);
  };

  const removeCustomPrinciple = (id: string) => {
    setCustomPrinciples((prev) => prev.filter((p) => p.id !== id));
    setSelectedPrinciples((prev) => prev.filter((p) => p !== id));
  };

  const addProcess = () => {
    if (!newProcess.trim()) return;
    setPrioritizedProcesses((prev) => [...prev, newProcess.trim()]);
    setNewProcess("");
  };

  const removeProcess = (index: number) => {
    setPrioritizedProcesses((prev) => prev.filter((_, i) => i !== index));
  };

  const addChallenge = () => {
    if (!newChallenge.trim()) return;
    setPrioritizedChallenges((prev) => [...prev, newChallenge.trim()]);
    setNewChallenge("");
  };

  const removeChallenge = (index: number) => {
    setPrioritizedChallenges((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    saveConfig({
      vision: visionStatement,
      objective,
      value_vision_archetype: selectedVision,
      selected_principles: selectedPrinciples,
      custom_principles: customPrinciples,
      strategic_plan: strategicPlan,
      prioritized_processes: prioritizedProcesses,
      prioritized_challenges: prioritizedChallenges.map((title) => ({ title })),
      objectives,
    } as any);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Cargando configuración...
        </CardContent>
      </Card>
    );
  }

  const activeVision = VALUE_VISIONS.find((v) => v.id === selectedVision);

  return (
    <div className="space-y-6">
      {/* 1. Value Vision Model */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            Modelo de Visión de Valor de la IA
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>Selecciona el arquetipo que mejor refleje cómo tu organización busca generar valor con IA: eficiencia operativa, agilidad de procesos, reinvención de producto o decisiones estratégicas.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>
            Selecciona el arquetipo de valor que mejor representa la estrategia de IA de tu organización
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUE_VISIONS.map((vision) => {
              const Icon = vision.icon;
              const isSelected = selectedVision === vision.id;
              return (
                <Card
                  key={vision.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    isSelected ? "ring-2 ring-primary border-primary" : ""
                  }`}
                  onClick={() => setSelectedVision(vision.id)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${vision.color}`}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-sm">{vision.name}</CardTitle>
                        <p className="text-xs text-muted-foreground">{vision.subtitle}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-xs">
                    <p className="text-muted-foreground">{vision.focus}</p>
                    <div className="flex flex-wrap gap-1 pt-1">
                      <Badge variant="outline" className="text-[10px]">
                        {vision.objective}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        Riesgo: {vision.risk}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        {vision.horizon}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {activeVision && (
            <Card className={`border ${activeVision.colorLight}`}>
              <CardContent className="pt-6 space-y-3">
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold mb-1">Descripción</p>
                    <p className="text-muted-foreground text-xs">{activeVision.description}</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Valor Principal</p>
                    <p className="text-muted-foreground text-xs">{activeVision.value}</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">KPI Clave</p>
                    <p className="text-muted-foreground text-xs">{activeVision.kpi}</p>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Horizonte de Valor</p>
                    <p className="text-muted-foreground text-xs">
                      {activeVision.horizon} · Perfil de riesgo: {activeVision.risk}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            <Label className="font-semibold">Redacte la Visión de IA de su organización</Label>
            <Textarea
              placeholder="Ej: Ser líderes en la transformación digital mediante la IA, optimizando operaciones, innovando productos y tomando decisiones basadas en datos para generar valor sostenible a todos nuestros stakeholders..."
              value={visionStatement}
              onChange={(e) => setVisionStatement(e.target.value)}
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* 2. Objective */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Objetivo del Centro de Excelencia
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>Redacta el objetivo principal y misión del CoE. Este objetivo guiará todas las decisiones e iniciativas de IA de la organización.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>
            Define el objetivo principal que guiará todas las iniciativas del CoE
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Ej: Institucionalizar la adopción responsable de IA en toda la organización, asegurando que cada iniciativa genere valor medible, cumpla con estándares éticos y regulatorios, y acelere la transformación digital de la empresa..."
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Objectives */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Objetivos Estratégicos del CoE
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>Lista los objetivos estratégicos específicos y medibles que el CoE debe lograr. Por ejemplo: reducir costos, acelerar time-to-market o mejorar la experiencia del cliente.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>
            Define los objetivos estratégicos que guiarán las iniciativas del Centro de Excelencia
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Ej: Reducir costos operativos 20% mediante automatización con IA"
              value={newObjective}
              onChange={(e) => setNewObjective(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newObjective.trim()) {
                  setObjectives(prev => [...prev, newObjective.trim()]);
                  setNewObjective('');
                }
              }}
            />
            <Button
              onClick={() => {
                if (newObjective.trim()) {
                  setObjectives(prev => [...prev, newObjective.trim()]);
                  setNewObjective('');
                }
              }}
              disabled={!newObjective.trim()}
              className="shrink-0 gap-1"
            >
              <Plus className="h-4 w-4" /> Agregar
            </Button>
          </div>
          {objectives.length > 0 ? (
            <div className="space-y-2">
              {objectives.map((obj, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                    {i + 1}
                  </div>
                  <span className="flex-1 text-sm font-medium">{obj}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => setObjectives(prev => prev.filter((_, idx) => idx !== i))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay objetivos definidos. Agregue los objetivos estratégicos del CoE.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 3. Principles */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                Principios Rectores
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p>Selecciona los principios éticos y de gobernanza que regirán el uso de IA en tu organización. Puedes agregar principios personalizados.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
              <CardDescription>
                Selecciona los principios que regirán el uso de IA en tu organización
              </CardDescription>
            </div>
            <Dialog open={newPrincipleOpen} onOpenChange={setNewPrincipleOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1">
                  <Plus className="h-4 w-4" /> Agregar Principio
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Nuevo Principio</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label>Nombre del principio</Label>
                    <Input
                      value={newPrincipleName}
                      onChange={(e) => setNewPrincipleName(e.target.value)}
                      placeholder="Ej: Resiliencia"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Descripción</Label>
                    <Textarea
                      value={newPrincipleDesc}
                      onChange={(e) => setNewPrincipleDesc(e.target.value)}
                      placeholder="Breve descripción del principio..."
                      rows={2}
                    />
                  </div>
                  <Button onClick={addCustomPrinciple} disabled={!newPrincipleName.trim()} className="w-full">
                    Agregar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...DEFAULT_PRINCIPLES, ...customPrinciples].map((principle) => {
              const isCustom = customPrinciples.some((c) => c.id === principle.id);
              return (
                <div
                  key={principle.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedPrinciples.includes(principle.id)
                      ? "bg-primary/5 border-primary/30"
                      : "hover:bg-muted/50"
                  }`}
                  onClick={() => togglePrinciple(principle.id)}
                >
                  <Checkbox
                    checked={selectedPrinciples.includes(principle.id)}
                    onCheckedChange={() => togglePrinciple(principle.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">{principle.label}</p>
                      {isCustom && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCustomPrinciple(principle.id);
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{principle.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {selectedPrinciples.length} principios seleccionados
          </p>
        </CardContent>
      </Card>

      {/* 4. Strategic Plan & KPI */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Plan Estratégico y KPIs
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>Comparte un resumen del plan estratégico de IA, incluyendo metas a corto, mediano y largo plazo, así como los KPIs que utilizarás para medir el éxito.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>
            Comparta un resumen del plan estratégico de IA, incluyendo objetivos estratégicos y KPIs clave
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Resuma aquí el plan estratégico de IA de su organización, incluyendo objetivos estratégicos, KPIs principales, metas a corto/mediano/largo plazo, y cualquier otra información relevante para guiar las recomendaciones del sistema..."
            value={strategicPlan}
            onChange={(e) => setStrategicPlan(e.target.value)}
            rows={6}
          />
        </CardContent>
      </Card>

      {/* 5. Prioritized Processes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Workflow className="h-5 w-5 text-primary" />
            Procesos Priorizados
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>Identifica los procesos de negocio que priorizarás para la transformación con IA. Por ejemplo: atención al cliente, gestión de inventarios o análisis financiero.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>
            Identifique los procesos de negocio priorizados para transformación con IA
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Ej: Atención al cliente, Gestión de inventarios, Análisis financiero..."
              value={newProcess}
              onChange={(e) => setNewProcess(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addProcess()}
            />
            <Button onClick={addProcess} disabled={!newProcess.trim()} className="shrink-0 gap-1">
              <Plus className="h-4 w-4" /> Agregar
            </Button>
          </div>
          {prioritizedProcesses.length > 0 ? (
            <div className="space-y-2">
              {prioritizedProcesses.map((process, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                    {i + 1}
                  </div>
                  <span className="flex-1 text-sm font-medium">{process}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeProcess(i)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay procesos priorizados. Agregue los procesos clave de su organización.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 6. Prioritized Challenges */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Retos Priorizados
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p>Liste los retos de negocio donde su organización espera que la IA aporte solución. Alimentan recomendaciones de políticas, procesos, herramientas y riesgos, y se ofrecen a los participantes en el Laboratorio de Retos.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <CardDescription>
            Retos de negocio donde se espera que la IA aporte solución (ej: reducir tiempos de atención, predecir demanda, automatizar back-office).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Ej: Reducir tiempo de respuesta al cliente, Predecir rotación de inventario..."
              value={newChallenge}
              onChange={(e) => setNewChallenge(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addChallenge()}
            />
            <Button onClick={addChallenge} disabled={!newChallenge.trim()} className="shrink-0 gap-1">
              <Plus className="h-4 w-4" /> Agregar
            </Button>
          </div>
          {prioritizedChallenges.length > 0 ? (
            <div className="space-y-2">
              {prioritizedChallenges.map((challenge, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                    {i + 1}
                  </div>
                  <span className="flex-1 text-sm font-medium">{challenge}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeChallenge(i)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay retos priorizados. Agregue los retos clave que espera resolver con IA.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} size="lg" className="gap-2" disabled={isSaving}>
          <Save className="h-4 w-4" /> {isSaving ? "Guardando..." : "Guardar Configuración Estratégica"}
        </Button>
      </div>
    </div>
  );
}
